import {
  Alert,
  BodyText,
  Button,
  Card,
  HeadingText,
  TextArea,
} from '@paypalcorp/pp-react'
import React, { FunctionComponent, useState } from 'react'
import './article-feedback.css'
import withAnalytics from '../../utils/withAnalytics'

const ArticleFeedback: FunctionComponent = ({}) => {
  const [isFbCaptured, setIsFbCaptured] = useState<boolean>(false)
  const [isFbRating, setIsFbRating] = useState<boolean>(false)
  const [isFbReason, setIsFbReason] = useState<boolean>(false)
  const [selectedReason, setSelectedReason] = useState<string>('')
  const [selectedRating, setSelectedRating] = useState<number>(0)
  const [reasonDescription, setReasonDescription] = useState<string>('')

  const fptiData = typeof window !== `undefined` && window.fpti

  enum FeedbackAnswers {
    YES = 'Y',
    NO = 'N',
  }

  const reasonMap: { [x: string]: string } = {
    toomuchinfo: 'Too much information',
    notenoughinfo: 'Not enough information to answer my question',
    lookingfor: 'This isn´t what i was looking for',
    incorrect: 'I tried this and it didn´t solve the problem',
    dontlike: 'I don´t like this feature or policy',
    other: 'Other',
  }

  const reasons: string[] = Object.keys(reasonMap)

  const HCAR_MAPPINGS: { [x: string]: string } = {
    toomuchinfo: 'N1',
    notenoughinfo: 'N2',
    lookingfor: 'N3',
    incorrect: 'N4',
    dontlike: 'N5',
    other: 'N99',
  }

  const dataMasking = (data: string) => {
    const maskRegEx =
      /(\d\d+|(([^<>()\[\]\.,;:\s@\"]+(\.[^<>()\[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,}))/g // eslint-disable-line
    const maskPattern = '#########'

    if (data) {
      return data.replace(maskRegEx, maskPattern)
    }

    return data
  }

  const recordAnalytics = (answer: FeedbackAnswers) => {
    if (fptiData && window.PAYPAL?.analytics?.instance) {
      let category = 'feedback'
      let hcar =
        answer === FeedbackAnswers.YES
          ? answer.toString()
          : HCAR_MAPPINGS[selectedReason]
      let hcaf = `${category}|${hcar}${
        selectedReason
          ? `|${selectedRating}|${dataMasking(reasonDescription)}`
          : ''
      }`

      window.PAYPAL.analytics.instance.record({
        data: {
          ...fptiData,
          page: 'main:developer:integration-builder',
          pgrp: 'main:developer:integration-builder',
          e: 'cl',
          hcar,
          hcaf,
          component: 'devdiscoverynodeweb',
        },
      })
    }
  }

  const fbAnswerHandler = (answer: FeedbackAnswers) => {
    if (answer === FeedbackAnswers.YES) {
      recordAnalytics(answer)
      setIsFbCaptured(true)
    } else {
      setIsFbRating(true)
    }
  }

  const fbRatingHandler = (rating: number) => {
    setSelectedRating(rating)
    setIsFbReason(true)
  }

  const fbFeedbackHandler = () => {
    recordAnalytics(FeedbackAnswers.NO)

    setIsFbRating(false)
    setIsFbReason(false)
    setIsFbCaptured(true)
  }

  const fbReasonHandler = (reason: string) => {
    setSelectedReason(reason)
  }

  const renderFbReason = () => {
    return isFbReason ? (
      <div className="articleFeedback__content">
        <Card className="hc_fb_reason">
          <BodyText className="hc_fb_reason__title" strong>
            Why wasn't this information helpful?
          </BodyText>
          <ul className="hc_fb_reason_options__section">
            {reasons.map((reason: string) => (
              <li key={`reason.${reason}`}>
                <BodyText
                  className={`hc_fb_reason_option__title${
                    reason === selectedReason ? ' selected' : ''
                  }`}
                  onClick={() => fbReasonHandler(reason)}
                >
                  {reasonMap[reason]}
                </BodyText>
                {reason === selectedReason ? (
                  <>
                    <TextArea
                      name="feedback_text"
                      className="hc_fb_reason_option__desc"
                      maxCharacters={100}
                      inputClassName="hc_fb_reason_option__desc--input"
                      showCharacterCounter={true}
                      placeholder={
                        'Tell us more - please don´t include personal info like your email or account numbers.'
                      }
                      value={reasonDescription}
                      onChange={(e) => setReasonDescription(e.target.value)}
                      data-testid="hc-fb-reason-option-desc"
                    />
                    <Button
                      className="hc_fb_reason_option__btn_submit"
                      fullWidthOnMobile={false}
                      size="sm"
                      data-testid="hc-fb-reason-option-btn-submit"
                      onClick={() => fbFeedbackHandler()}
                    >
                      Send
                    </Button>
                  </>
                ) : null}
              </li>
            ))}
          </ul>
        </Card>
      </div>
    ) : null
  }

  const renderFbSection = () => {
    return !isFbCaptured && !isFbRating ? (
      <div className="articleFeedback__content">
        <HeadingText size="sm">
          Was the integration builder helpful?
        </HeadingText>
        <div className="articleFeedback__buttons">
          <Button
            secondary
            onClick={() => fbAnswerHandler(FeedbackAnswers.YES)}
          >
            Yes
          </Button>
          <Button secondary onClick={() => fbAnswerHandler(FeedbackAnswers.NO)}>
            No
          </Button>
        </div>
      </div>
    ) : null
  }

  const renderFbReceived = () => {
    return isFbCaptured ? (
      <div className="articleFeedback__content">
        <Alert className="articleFeedback_alert" type="success">
          Feedback sent
        </Alert>
      </div>
    ) : null
  }

  const renderFbRating = () => {
    return isFbRating ? (
      <div className="articleFeedback__content vertical">
        <HeadingText size="sm">
          Please rate your experience with this tool based on how useful it is
        </HeadingText>

        <div className="articleFeedback__buttons">
          <Button
            secondary={selectedRating !== 1}
            primary={selectedRating === 1}
            onClick={() => fbRatingHandler(1)}
          >
            1
          </Button>
          <Button
            secondary={selectedRating !== 2}
            primary={selectedRating === 2}
            onClick={() => fbRatingHandler(2)}
          >
            2
          </Button>
          <Button
            secondary={selectedRating !== 3}
            primary={selectedRating === 3}
            onClick={() => fbRatingHandler(3)}
          >
            3
          </Button>
          <Button
            secondary={selectedRating !== 4}
            primary={selectedRating === 4}
            onClick={() => fbRatingHandler(4)}
          >
            4
          </Button>
          <Button
            secondary={selectedRating !== 5}
            primary={selectedRating === 5}
            onClick={() => fbRatingHandler(5)}
          >
            5
          </Button>
        </div>

        {renderFbReason()}
      </div>
    ) : null
  }

  return (
    <div className="articleFeedback__container">
      {renderFbSection()}
      {renderFbReceived()}
      {renderFbRating()}
    </div>
  )
}

export default withAnalytics(ArticleFeedback)
